// Shared loader for the SS character list used by
// `CharacterPicker` and `resolveCharacterByName` consumers.
//
// SS characters get their display name from the .md frontmatter via
// `/api/short-story/all-characters`; the extension endpoint contributes
// `voicePresetId`. The two SS lists are joined by unified id
// (`ss:<slug>:<kebab>`). SS characters missing from all-characters (e.g.
// an extension row with no matching .md file) fall back to the kebab.

import type { CharacterKey, CharacterLite } from './types';

interface SsExtensionListItem {
  id: string;
  voicePresetId?: string | null;
}

interface SsAllCharacterItem {
  id: string;
  name: string;
}

export async function loadAllCharacters(): Promise<CharacterLite[]> {
  const [ssExtRes, ssAllRes] = await Promise.all([
    fetch('/api/short-story-character-extension?all=true&list=true'),
    fetch('/api/short-story/all-characters'),
  ]);

  const ssExtList = ((await ssExtRes.json()).extensions ?? []) as SsExtensionListItem[];
  const ssAllList = ((await ssAllRes.json()).characters ?? []) as SsAllCharacterItem[];

  // Name map keyed by unified id
  const ssNameById = new Map<string, string>();
  for (const c of ssAllList) ssNameById.set(c.id, c.name);

  const ssLite: CharacterLite[] = ssExtList.flatMap((e) => {
    const m = e.id.match(/^ss:([^:]+):(.+)$/);
    if (!m) return [];
    const key: CharacterKey = { kind: 'ss', slug: m[1], kebab: m[2] };
    return [{
      key,
      name: ssNameById.get(e.id) ?? m[2],  // fallback to kebab if md missing
      voicePresetId: e.voicePresetId ?? null,
    }];
  });

  // Include SS characters that have an .md file but no extension row yet
  // (never had a voice preset picked). Otherwise they wouldn't be selectable.
  const knownSsIds = new Set(ssExtList.map((e) => e.id));
  const orphanSsLite: CharacterLite[] = ssAllList
    .filter((c) => !knownSsIds.has(c.id))
    .flatMap((c) => {
      const m = c.id.match(/^ss:([^:]+):(.+)$/);
      if (!m) return [];
      return [{
        key: { kind: 'ss' as const, slug: m[1], kebab: m[2] },
        name: c.name,
        voicePresetId: null,
      }];
    });

  return [...ssLite, ...orphanSsLite];
}
